import { Box, Heading, Text, TextArea, VStack } from 'native-base'
import { FormikProps } from 'formik'

import { colors } from '../../utils/theme/configureTheme'
import ExperienceRatingSmiley from '../ExperienceRatingSmiley'
import OptionChips from './OptionChips'
import ThumbsRating from './ThumbsRating'
import { FeedbackFormValues, DRIVER_PRAISE_OPTIONS, NEGATIVE_EXPERIENCE_OPTIONS, TRACKING_ISSUE_OPTIONS } from './types'

interface DeliveryExperienceSectionProps {
  formikProps: FormikProps<FeedbackFormValues>
  isDelivered: boolean
}

/** Styled card wrapper used to visually group individual feedback questions. */
export function QuestionCard({ children }: { children: React.ReactNode }) {
  return (
    <Box backgroundColor={colors.brand.white} borderRadius={12} p={4} borderWidth={1} borderColor={colors.gray[200]}>
      {children}
    </Box>
  )
}

/** Feedback form section for rating overall delivery, driver satisfaction, and tracking experience. */
export default function DeliveryExperienceSection({ formikProps, isDelivered }: DeliveryExperienceSectionProps) {
  const { values, setFieldValue } = formikProps

  return (
    <Box px={4} pb={4}>
      <Heading fontSize={18} fontWeight="600" fontFamily="bodySemiBold" color={colors.brand.vermillion} mb={3}>
        Your Delivery Experience
      </Heading>
      <VStack space={3}>
        {/* Overall delivery experience */}
        <QuestionCard>
          <ExperienceRatingSmiley
            onSmileyPress={rating => {
              setFieldValue('overallDeliveryExperience', rating)
              if (rating > 2) {
                setFieldValue('negativeExperienceReasons', [])
              }
            }}
            existingRating={values.overallDeliveryExperience ?? undefined}
          />
          {values.overallDeliveryExperience !== null && values.overallDeliveryExperience <= 2 && (
            <OptionChips
              options={NEGATIVE_EXPERIENCE_OPTIONS}
              selected={values.negativeExperienceReasons}
              onChange={reasons => setFieldValue('negativeExperienceReasons', reasons)}
              columns={2}
              fontSize={13}
              numberOfLines={1}
            />
          )}
        </QuestionCard>

        {/* Driver satisfaction — only shown if package was delivered */}
        {isDelivered && (
          <QuestionCard>
            <Text fontSize={14} fontWeight="500" textAlign="center" fontFamily="Matter Regular" mb={1}>
              How satisfied were you with your delivery driver?
            </Text>
            <ThumbsRating
              value={values.driverSatisfaction}
              onChange={val => {
                setFieldValue('driverSatisfaction', val)
                if (val === false) {
                  setFieldValue('driverIssueReasons', [])
                }
              }}
            />
            {values.driverSatisfaction === true && (
              <OptionChips
                options={DRIVER_PRAISE_OPTIONS}
                selected={values.driverIssueReasons}
                onChange={reasons => setFieldValue('driverIssueReasons', reasons)}
              />
            )}
          </QuestionCard>
        )}

        {/* App/site tracking experience */}
        <QuestionCard>
          <Text fontSize={14} fontWeight="500" textAlign="center" fontFamily="Matter Regular" mb={1}>
            How was your experience using Veho's app or site to track your package?
          </Text>
          <ThumbsRating
            value={values.trackingExperience}
            onChange={val => {
              setFieldValue('trackingExperience', val)
              if (val === true) {
                setFieldValue('trackingIssueReasons', [])
              }
            }}
          />
          {values.trackingExperience === false && (
            <OptionChips
              options={TRACKING_ISSUE_OPTIONS}
              selected={values.trackingIssueReasons}
              onChange={reasons => setFieldValue('trackingIssueReasons', reasons)}
            />
          )}
        </QuestionCard>

        {/* Additional comments */}
        <Box mt={1}>
          <Text fontSize={14} fontWeight="500" fontFamily="Matter Regular" mb={2}>
            Any additional information you want to provide?
          </Text>
          {/* @ts-expect-error missing tvParallaxProperties, onTextInput */}
          <TextArea
            value={values.additionalDeliveryComments}
            onChangeText={text => setFieldValue('additionalDeliveryComments', text)}
            placeholder="Tell us more about your delivery experience..."
            autoCompleteType={undefined}
            h={100}
            borderWidth={2}
            borderColor={colors.brand.softBlack}
            borderRadius={8}
            fontSize={14}
            fontFamily="Matter Regular"
            p={3}
          />
        </Box>
      </VStack>
    </Box>
  )
}
